import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';
import React from 'react';
import { OrgApp, RootRedirect } from './org-app';
import { Loading } from './components';
import { CoteraDuckDB } from './etc';
import { WorkOSLogin, WorkOSSignup } from './pages/workos';
import { CliLoginPage } from './pages/cli-login';
import { Button } from '@cotera/client/v0/components/ui/button';
import { useBootSequence } from './hooks/use-boot-sequence';
import { useLogout } from './hooks/use-logout';
import type { Org, Whoami } from '@cotera/api';
import { useAuth } from '@workos-inc/authkit-react';

export const App: React.FC<{
  initedDb: Promise<CoteraDuckDB>;
}> = ({ initedDb }) => {
  const boot = useBootSequence();

  if (boot.status === 'loading') {
    return (
      <Loading.Boot
        footer={
          boot.retrying ? (
            <p className="text-sm text-muted-foreground">
              Things are taking a bit longer than usual, bear with us!
            </p>
          ) : undefined
        }
      />
    );
  }

  if (boot.status === 'error') {
    return (
      <Loading.Boot
        footer={
          <div className="flex flex-col items-center gap-3">
            <p className="text-sm text-muted-foreground">{boot.error}</p>
            <div className="flex gap-3">
              <Button size="sm" variant="outline" onClick={boot.retry}>
                Try again
              </Button>
              <LogoutButton />
            </div>
          </div>
        }
      />
    );
  }

  if (boot.status === 'unexpected-error') {
    return (
      <Loading.Boot
        message={
          <div className="flex flex-col items-center gap-3">
            <p className="text-sm text-muted-foreground">
              We're having some difficulty right now, please try again in a few
              minutes
            </p>
            <LogoutButton />
          </div>
        }
      />
    );
  }

  if (boot.status === 'logged-out') {
    return <LoggedOutRoutes />;
  }

  // status === 'ready'
  return (
    <Routes>
      <Route
        path="/share/:token"
        element={<ShareLinkRouteRedirect whoami={boot.whoami} />}
      />
      <Route path="/cli-login" element={<CliLoginPage />} />
      <Route path="/onboarding" element={<Navigate replace to="/" />} />
      <Route path="/login" element={<LoginRedirect />} />
      <Route path="/signup" element={<Navigate replace to="/" />} />
      <Route path="/u/*" element={<LegacyUrlRedirect />} />
      <Route
        path="/:orgName/*"
        element={<OrgApp whoami={boot.whoami} initedDb={initedDb} />}
      />
      <Route path="/*" element={<RootRedirect whoami={boot.whoami} />} />
    </Routes>
  );
};

function getPreferredOrg(
  whoami: Whoami,
  workosOrgId: string | null
): Org | undefined {
  let org: Org | undefined;
  if (workosOrgId !== null) {
    org = whoami.orgs.find((o) => o.workosId === workosOrgId);
  }
  if (org === undefined) {
    const lsOrgId = localStorage.getItem('COTERA_SELECTED_ORG_ID');
    if (lsOrgId !== null) {
      org = whoami.orgs.find((o) => o.id === lsOrgId);
    }
  }
  return org ?? whoami.orgs[0];
}

const ShareLinkRouteRedirect: React.FC<{ whoami: Whoami }> = ({ whoami }) => {
  const { token } = useParams<{ token: string }>();
  const { organizationId: workosOrgId } = useAuth();
  const org = getPreferredOrg(whoami, workosOrgId);

  if (token === undefined || org === undefined) {
    return <Navigate replace to="/" />;
  }

  return <Navigate replace to={`/${org.slug}/share/${token}`} />;
};

const IGNORED_RETURN_TO_PATHS = ['signup', 'errorType'];

/** Redirect legacy /u/<slug>/... URLs to /<slug>/... */
const LegacyUrlRedirect: React.FC = () => {
  const location = useLocation();
  const newPath =
    location.pathname.replace(/^\/u/, '') + location.search + location.hash;
  return <Navigate to={newPath} replace />;
};

const LoggedOutRoutes = () => {
  const getReturnTo = () => {
    if (
      IGNORED_RETURN_TO_PATHS.some((path) =>
        window.location.pathname.includes(path)
      )
    ) {
      return '/';
    }
    return window.location.pathname + window.location.search;
  };

  return (
    <Routes>
      <Route path="/login" element={<WorkOSLogin whoamiFailed={true} />} />
      <Route path="/signup" element={<WorkOSSignup />} />
      {/* CLI login redirects to regular login with returnTo */}
      <Route
        path="/cli-login"
        element={<Navigate replace to={`/login?returnTo=${getReturnTo()}`} />}
      />
      <Route
        path="/*"
        element={<Navigate replace to={`/login?returnTo=${getReturnTo()}`} />}
      />
    </Routes>
  );
};

const LogoutButton: React.FC = () => {
  const { logout } = useLogout();
  return (
    <Button size="sm" variant="ghost" onClick={() => logout()}>
      Logout
    </Button>
  );
};

/** Handle /login when user is already authenticated - respects returnTo parameter */
const LoginRedirect: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const returnTo = searchParams.get('returnTo');

  // If there's a returnTo, store it and redirect there
  if (returnTo) {
    // Use window.location for server routes, Navigate for client routes
    if (returnTo.startsWith('/cli-login')) {
      window.location.href = returnTo;
      return null;
    }
    return <Navigate replace to={returnTo} />;
  }

  // No returnTo, go to home
  return <Navigate replace to="/" />;
};
